import { useEffect, useRef, useState } from "preact/hooks"
import { format, parseISO } from "date-fns"
import { marked } from "marked"
import { Archive, ArrowUp, Loader2, MessageSquare, Plus, Wand2, X } from "lucide-react"
import { Button } from "../../../../components/ui/button"
import { Textarea } from "../../../../components/ui/textarea"
import { toast } from "../../../../components/ui/use-toast"
import {
  createTradePromotionChatConversation,
  getTradePromotionChatMessages,
  listTradePromotionChatConversations,
  sendTradePromotionChatMessageStream,
  TradePromotionChatConversation,
  TradePromotionChatMessage,
  updateTradePromotionChatConversation,
} from "../../../../api/trade_promotion_chat"

interface TradePromotionChatPanelProps {
  open: boolean
  onClose: () => void
  currentYear?: number
  currentSearch?: string
}

const EXAMPLE_PROMPTS = [
  "Which promotion types performed best this year?",
  "Which retailers overspent plan?",
  "Where do we lack enough data to judge performance?",
]

marked.setOptions({ breaks: true, gfm: true })

export function TradePromotionChatPanel({ open, onClose, currentYear, currentSearch }: TradePromotionChatPanelProps) {
  const [conversation, set_conversation] = useState<TradePromotionChatConversation | null>(null)
  const [conversations, set_conversations] = useState<TradePromotionChatConversation[]>([])
  const [messages, set_messages] = useState<TradePromotionChatMessage[]>([])
  const [input_value, set_input_value] = useState("")
  const [loading, set_loading] = useState(false)
  const [sending, set_sending] = useState(false)
  const [streaming_status, set_streaming_status] = useState("")
  const stream_abort_ref = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!open) {
      stream_abort_ref.current?.abort()
      return
    }
    load_conversation()
    return () => stream_abort_ref.current?.abort()
  }, [open])

  async function load_conversation() {
    set_loading(true)
    try {
      const existing = await listTradePromotionChatConversations()
      const next = existing[0] ?? await createTradePromotionChatConversation({})
      set_conversations(existing.length > 0 ? existing : [next])
      await select_conversation(next)
    } catch (error) {
      toast({
        title: "TradeWiz unavailable",
        description: error instanceof Error ? error.message : "Could not load chat.",
        variant: "destructive",
      })
    } finally {
      set_loading(false)
    }
  }

  async function select_conversation(next: TradePromotionChatConversation) {
    set_conversation(next)
    set_messages(await getTradePromotionChatMessages(next.id))
    set_streaming_status("")
  }

  async function handle_new_chat() {
    if (sending) return
    set_loading(true)
    try {
      const next = await createTradePromotionChatConversation({ force_new: true })
      set_conversations((prev: TradePromotionChatConversation[]) => [next, ...prev])
      await select_conversation(next)
      set_input_value("")
    } catch (error) {
      toast({
        title: "Could not start chat",
        description: error instanceof Error ? error.message : "Failed to create chat.",
        variant: "destructive",
      })
    } finally {
      set_loading(false)
    }
  }

  async function handle_archive_chat(target: TradePromotionChatConversation) {
    if (sending) return
    try {
      await updateTradePromotionChatConversation(target.id, { archived: true })
      const remaining = conversations.filter((item: TradePromotionChatConversation) => item.id !== target.id)
      set_conversations(remaining)
      if (target.id === conversation?.id) {
        if (remaining.length > 0) await select_conversation(remaining[0])
        else await handle_new_chat()
      }
    } catch (error) {
      toast({
        title: "Could not archive chat",
        description: error instanceof Error ? error.message : "Failed to archive chat.",
        variant: "destructive",
      })
    }
  }

  function handle_key_down(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault()
      handle_send_message()
    }
  }

  async function handle_send_message() {
    if (!conversation || !input_value.trim() || sending) return
    const user_content = input_value.trim()
    const assistant_id = `stream-${Date.now()}`
    const user_message: TradePromotionChatMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: conversation.id,
      role: "user",
      content: user_content,
      created_at: new Date().toISOString(),
    }
    const assistant_message: TradePromotionChatMessage = {
      id: assistant_id,
      conversation_id: conversation.id,
      role: "assistant",
      content: "",
      created_at: new Date().toISOString(),
    }
    set_input_value("")
    set_sending(true)
    set_streaming_status("Reading promotion data...")
    set_messages((prev: TradePromotionChatMessage[]) => [...prev, user_message, assistant_message])

    try {
      stream_abort_ref.current?.abort()
      await new Promise<void>((resolve, reject) => {
        stream_abort_ref.current = sendTradePromotionChatMessageStream(
          conversation.id,
          { content: user_content, current_year: currentYear, current_search: currentSearch },
          {
            onThinking: (message: string) => set_streaming_status(message),
            onToolCall: (toolName: string) => {
              const status = trade_promotion_tool_status(toolName)
              set_streaming_status(status)
              set_messages((prev: TradePromotionChatMessage[]) => prev.map((message: TradePromotionChatMessage) => (
                message.id === assistant_id && !message.content ? { ...message, content: `${status}...` } : message
              )))
            },
            onTextDelta: (content: string) => {
              set_streaming_status("Writing the answer...")
              set_messages((prev: TradePromotionChatMessage[]) => prev.map((message: TradePromotionChatMessage) => (
                message.id === assistant_id
                  ? { ...message, content: is_status_placeholder(message.content) ? content : message.content + content }
                  : message
              )))
            },
            onText: (content: string) => {
              set_messages((prev: TradePromotionChatMessage[]) => prev.map((message: TradePromotionChatMessage) => (
                message.id === assistant_id ? { ...message, content } : message
              )))
            },
            onDone: () => {
              set_streaming_status("")
              resolve()
            },
            onError: (error: string) => reject(new Error(error)),
          },
        )
      })
      const refreshed = await listTradePromotionChatConversations()
      set_conversations(refreshed)
    } catch (error) {
      set_messages((prev: TradePromotionChatMessage[]) => prev.filter((message: TradePromotionChatMessage) => message.id !== user_message.id && message.id !== assistant_id))
      toast({
        title: "TradeWiz failed",
        description: error instanceof Error ? error.message : "Could not send message.",
        variant: "destructive",
      })
    } finally {
      set_sending(false)
      stream_abort_ref.current = null
    }
  }

  if (!open) return null

  return (
    <div class="fixed inset-0 z-[10000] flex justify-end bg-black/20" onClick={onClose}>
      <aside class="flex h-full w-full max-w-5xl bg-white shadow-xl" onClick={(event: MouseEvent) => event.stopPropagation()}>
        <div class="hidden w-72 shrink-0 border-r border-gray-200 bg-gray-50 p-4 md:block">
          <div class="mb-3 flex items-center justify-between">
            <div class="text-sm font-semibold text-gray-900">Previous chats</div>
            <Button variant="outline" size="sm" onClick={handle_new_chat} disabled={loading || sending}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div class="space-y-2">
            {conversations.map((item: TradePromotionChatConversation) => (
              <button
                key={item.id}
                class={`w-full rounded-md border px-3 py-2 text-left text-sm ${item.id === conversation?.id ? "border-blue-300 bg-white" : "border-gray-200 bg-gray-50 hover:bg-white"}`}
                onClick={() => select_conversation(item)}
              >
                <div class="line-clamp-2 font-medium text-gray-900">{conversation_title(item)}</div>
                <div class="mt-1 text-xs text-gray-500">{format(parseISO(item.updated_at), "MMM d, h:mm a")}</div>
              </button>
            ))}
          </div>
        </div>

        <div class="flex min-w-0 flex-1 flex-col">
          <div class="flex items-center justify-between border-b border-gray-200 px-5 py-4">
            <div class="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-blue-700" />
              <div>
                <h2 class="text-base font-semibold text-gray-900">TradeWiz</h2>
                <div class="text-xs text-gray-500">Ask about calendars, spend, lift, ROI, gaps, and what to repeat.</div>
              </div>
            </div>
            <div class="flex items-center gap-2">
              {conversation ? (
                <Button variant="outline" size="sm" onClick={() => handle_archive_chat(conversation)} disabled={sending}>
                  <Archive className="h-4 w-4" />
                </Button>
              ) : null}
              <Button variant="outline" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto px-5 py-4">
            {loading && messages.length === 0 ? (
              <div class="flex items-center justify-center py-16 text-sm text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading chats...
              </div>
            ) : messages.length === 0 ? (
              <div class="mx-auto max-w-2xl py-12">
                <div class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <MessageSquare className="h-5 w-5 text-blue-700" />
                  Start with a calendar question
                </div>
                <div class="grid gap-2">
                  {EXAMPLE_PROMPTS.map((prompt: string) => (
                    <button
                      key={prompt}
                      class="rounded-md border border-gray-200 px-3 py-2 text-left text-sm text-gray-800 hover:border-blue-300 hover:bg-blue-50"
                      onClick={() => set_input_value(prompt)}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div class="mx-auto max-w-3xl space-y-4">
                {messages.map((message: TradePromotionChatMessage) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </div>
            )}
          </div>

          <div class="border-t border-gray-200 p-4">
            {streaming_status ? <div class="mb-2 text-xs text-blue-700">{streaming_status}</div> : null}
            <div class="flex gap-2">
              <Textarea
                value={input_value}
                onInput={(event: Event) => set_input_value((event.target as HTMLTextAreaElement).value)}
                onKeyDown={handle_key_down}
                placeholder="Ask about promotion performance..."
                className="min-h-[54px] flex-1"
              />
              <Button onClick={handle_send_message} disabled={sending || !input_value.trim()}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  )
}

function MessageBubble({ message }: { message: TradePromotionChatMessage }) {
  const is_user = message.role === "user"
  const html = is_user ? "" : marked.parse(escape_raw_html(message.content)) as string
  return (
    <div class={`flex ${is_user ? "justify-end" : "justify-start"}`}>
      <div class={`max-w-[85%] rounded-md px-3 py-2 text-sm ${is_user ? "bg-blue-700 text-white" : "bg-gray-100 text-gray-900"}`}>
        {is_user ? (
          <div class="whitespace-pre-wrap break-words">{message.content}</div>
        ) : (
          <div class="trade-promotion-chat-markdown break-words" dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </div>
    </div>
  )
}

export function escape_raw_html(content: string): string {
  return content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

function conversation_title(conversation: Pick<TradePromotionChatConversation, "title">): string {
  return conversation.title?.trim() || "New trade promotion chat"
}

export function trade_promotion_tool_status(toolName: string): string {
  switch (toolName) {
    case "list_calendars":
      return "Checking calendars"
    case "search_promotions":
      return "Searching promotions"
    case "get_promotion_effectiveness":
      return "Reading promotion effectiveness"
    case "compare_promotions":
      return "Comparing promotions"
    case "rank_promotions_over_time":
      return "Ranking promotions"
    case "aggregate_calendar_effectiveness":
      return "Aggregating calendar performance"
    case "get_data_coverage":
      return "Checking data coverage"
    case "list_syndicated_reports":
      return "Checking syndicated reports"
    case "get_syndicated_effectiveness_coverage":
      return "Checking syndicated coverage"
    case "explain_syndicated_match":
      return "Explaining syndicated match"
    default:
      return "Analyzing trade promotion data"
  }
}

function is_status_placeholder(content: string): boolean {
  return content.endsWith("...") && !content.includes("\n")
}
